// Loyalty scheme + card design, stored per owner (salon or self-employed).
//
// BACKED BY THE API since June 2026 (v2/loyalty/*) — localStorage is the
// cache; call hydrateLoyalty() on mount (LoyaltyContent does) and mutations
// write through. The earning/redemption engine is LIVE backend-side:
// stamps/points on completed appointments (election-aware), auto-reverse on
// re-status, wallet + till redemption endpoints. "live" means clients are
// earning.
//
// {
//   id, name, status: 'live' | 'paused',
//   mode: 'stamps' | 'points',
//   // stamps
//   stampsRequired: 6, rewardLabel: 'Free blow dry',
//   // points
//   pointsPerPound: 1, pointsTarget: 500, pointsLabel: 'Free treatment',
//   minSpend: null,
//   design: { themeId, icon, layout, fillStyle },
//   createdAt, updatedAt
// }

import { defaultDesign } from './data';
import { marketingBackend } from '../marketing-v2/services/marketingBackend';

// Per location: the salon scheme is each location's own, so one cache for
// every site showed site A's card at B until the next hydrate.
const storageKey = (ownerId) => {
  let site = '';
  try { site = localStorage.getItem('salonId') || ''; } catch (e) { /* no storage */ }
  return `beu-loyalty-${ownerId || 'default'}${site ? `-${site}` : ''}`;
};

export const loadScheme = (ownerId) => {
  try {
    const raw = localStorage.getItem(storageKey(ownerId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const fromServer = (s) => (s ? {
  id: s._id || s.id,
  name: s.name,
  status: s.status,
  mode: s.mode,
  stampsRequired: s.stampsRequired,
  rewardLabel: s.rewardLabel,
  pointsPerPound: s.pointsPerPound,
  pointsTarget: s.pointsTarget,
  pointsLabel: s.pointsLabel,
  earnRule: s.earnRule,
  minSpend: s.minSpend,
  earnScope: s.earnScope,
  earnItems: s.earnItems,
  dailyLimit: s.dailyLimit,
  redeemScope: s.redeemScope,
  redeemItems: s.redeemItems,
  rewardValueMode: s.rewardValueMode,
  rewardCap: s.rewardCap,
  design: s.design || defaultDesign(),
  createdAt: s.createdAt,
  updatedAt: s.updatedAt,
} : null);

/**
 * Pull the caller's scheme + election state from the backend into the
 * cache. Returns { scheme, election, salonScheme } — the election feeds the
 * sharing cache too (joinSalonScheme = my standing join).
 */
export const hydrateLoyalty = async (ownerId) => {
  try {
    const server = await marketingBackend.loyaltyGet();
    const scheme = fromServer(server.scheme);
    if (scheme) {
      try { localStorage.setItem(storageKey(ownerId), JSON.stringify(scheme)); } catch (e) { /* ignore */ }
    } else {
      try { localStorage.removeItem(storageKey(ownerId)); } catch (e) { /* ignore */ }
    }
    if (server.election) {
      saveSharingCache(ownerId, { joinSalonScheme: server.election.joined === true });
    }
    return { scheme, election: server.election || null, salonScheme: server.salonScheme || null };
  } catch (e) {
    console.error('loyalty hydrate failed (cache kept):', e.message);
    return { scheme: loadScheme(ownerId), election: null, salonScheme: null };
  }
};

/**
 * Owner side: pull the full self-employed roster + election state from the
 * backend (the same roster the marketing-settings page renders). Caches the
 * team (id + name) so teamForSharing() no longer depends on the user blob,
 * plus per-stylist offered/joined state.
 */
export const hydrateElections = async (ownerId) => {
  try {
    const server = await marketingBackend.loyaltyElections();
    const elections = server.elections || [];
    const perStylist = {};
    const team = [];
    elections.forEach((e) => {
      const id = String(e.stylistUserId);
      perStylist[id] = { offered: e.offered === true, joined: e.joined === true };
      team.push({ id, name: e.name || 'Stylist' });
    });
    saveSharingCache(ownerId, {
      offerToSelfEmployed: elections.some((e) => e.offered),
      perStylist,
      team,
    });
    return elections;
  } catch (e) {
    console.error('loyalty elections hydrate failed:', e.message);
    return [];
  }
};

/**
 * Writes the cache, posts to the server, returns BOTH the optimistic copy and
 * the in-flight promise. saveScheme keeps the old fire-and-forget contract on
 * top of it; saveSchemeAsync exposes the promise.
 */
const _saveScheme = (ownerId, scheme, extra = {}) => {
  const now = new Date().toISOString();
  const stored = {
    status: 'paused',
    design: defaultDesign(),
    ...scheme,
    id: scheme.id || `loy-${Date.now()}`,
    createdAt: scheme.createdAt || now,
    updatedAt: now,
  };
  try { localStorage.setItem(storageKey(ownerId), JSON.stringify(stored)); } catch (e) { /* ignore */ }
  const inflight = marketingBackend.loyaltySave({
    name: stored.name,
    status: stored.status,
    mode: stored.mode,
    stampsRequired: stored.stampsRequired,
    rewardLabel: stored.rewardLabel,
    pointsPerPound: stored.pointsPerPound,
    pointsTarget: stored.pointsTarget,
    pointsLabel: stored.pointsLabel,
    earnRule: stored.earnRule,
    minSpend: stored.minSpend,
    earnScope: stored.earnScope,
    earnItems: stored.earnItems,
    dailyLimit: stored.dailyLimit,
    redeemScope: stored.redeemScope,
    redeemItems: stored.redeemItems,
    rewardValueMode: stored.rewardValueMode,
    rewardCap: stored.rewardCap,
    design: stored.design,
    ...extra,
  }).then((server) => {
    // Merge the server echo over the optimistic copy so reward-value fields
    // (and any field the backend doesn't yet round-trip) survive until it does.
    const fresh = fromServer(server.scheme);
    if (fresh) {
      const echoed = Object.fromEntries(Object.entries(fresh).filter(([, v]) => v !== undefined));
      try { localStorage.setItem(storageKey(ownerId), JSON.stringify({ ...stored, ...echoed })); } catch (e) { /* ignore */ }
    }
    if (Array.isArray(server && server.appliedTo)) return { ...stored, appliedTo: server.appliedTo };
    return stored;
  });
  return { stored, inflight };
};

/**
 * The original contract, unchanged for the loyalty page: the cache is written
 * first, the server round-trip is fire-and-forget, and the optimistic copy
 * comes back synchronously.
 *
 * KNOWN FLAW, left alone deliberately because this is a live screen and the
 * change wants its own review: a failed save is logged and nothing else. The
 * caller already holds `stored`, so the page reports a save that never
 * reached the server, and the optimistic cache entry survives to be read back
 * later as though it were real. That is how the assistant came to offer a
 * card the database had never heard of. Prefer saveSchemeAsync.
 */
export const saveScheme = (ownerId, scheme, { onSynced } = {}) => {
  const { stored, inflight } = _saveScheme(ownerId, scheme);
  inflight
    // `onSynced`: run once the server has it (e.g. load a new scheme's stats,
    // which the server can only answer after the scheme exists there).
    .then(() => { if (onSynced) onSynced(); })
    .catch((e) => console.error('loyalty save failed:', e.message));
  return stored;
};

/**
 * The same save, awaitable, and honest about failing.
 *
 * Rejects when the server did not take it, and rolls the optimistic cache
 * entry back to whatever was there before — so a failure cannot leave behind
 * a scheme that exists only in this browser. Use this anywhere the person is
 * told the save worked.
 */
/**
 * Save this location's scheme AND copy it to the owner's other locations
 * (each gets its own scheme; cards already earned anywhere are untouched).
 * Resolves with the ids of the locations it was copied to.
 */
export const applySchemeToAllLocations = (ownerId, scheme) => {
  const { inflight } = _saveScheme(ownerId, scheme, { applyToAllLocations: true });
  return inflight;
};

export const saveSchemeAsync = (ownerId, scheme) => {
  const before = (() => {
    try { return localStorage.getItem(storageKey(ownerId)); } catch (e) { return null; }
  })();
  return _saveScheme(ownerId, scheme).inflight.catch((e) => {
    try {
      if (before === null) localStorage.removeItem(storageKey(ownerId));
      else localStorage.setItem(storageKey(ownerId), before);
    } catch (err) { /* ignore */ }
    throw e;
  });
};

// honour=true keeps already-earned rewards redeemable at the till; false voids
// them. Defaults to honour (the client earned them).
export const deleteScheme = (ownerId, honour = true) => {
  try { localStorage.removeItem(storageKey(ownerId)); } catch (e) { /* ignore */ }
  marketingBackend.loyaltyRemove(honour !== false).catch((e) => console.error('loyalty remove failed:', e.message));
  return null;
};

// In-flight rewards on the caller's scheme, for the delete-confirmation modal.
export const getSchemeInflight = () =>
  marketingBackend.loyaltySchemeInflight().catch(() => ({ rewardsReady: 0, clientsWithRewards: 0, clientsInProgress: 0 }));

// Salon-wide loyalty stats for the "How it is doing" panel (loyalty/stats).
// Null when they couldn't be loaded, so the panel says so instead of showing
// zeros as if they were real.
export const getSchemeStats = () =>
  marketingBackend.loyaltyStats().then((s) => s || null).catch(() => null);

// New schemes go live the moment they are created, so setting one up
// actually does something (a paused-by-default scheme reads as "not working").
export const blankScheme = (mode) => (mode === 'points'
  ? {
    name: 'Loyalty points',
    status: 'live',
    mode: 'points',
    pointsPerPound: 1,
    pointsTarget: 500,
    pointsLabel: 'Free treatment',
    design: defaultDesign(),
  }
  : {
    name: 'Stamp card',
    status: 'live',
    mode: 'stamps',
    stampsRequired: 6,
    rewardLabel: 'Free blow dry',
    design: defaultDesign(),
  });

// Dual opt-in for salon-managed loyalty (the marketing-consent pattern):
// the owner offers, each self-employed stylist accepts. Both revocable.
const sharingKey = (ownerId) => `beu-loyalty-sharing-${ownerId || 'default'}`;
export const loadSharing = (ownerId) => {
  try { return { offerToSelfEmployed: false, joinSalonScheme: false, ...(JSON.parse(localStorage.getItem(sharingKey(ownerId))) || {}) }; }
  catch (e) { return { offerToSelfEmployed: false, joinSalonScheme: false }; }
};
const saveSharingCache = (ownerId, patch) => {
  const next = { ...loadSharing(ownerId), ...patch };
  try { localStorage.setItem(sharingKey(ownerId), JSON.stringify(next)); } catch (e) { /* ignore */ }
  return next;
};
export const saveSharing = (ownerId, patch) => {
  const prev = loadSharing(ownerId);
  const next = saveSharingCache(ownerId, patch);
  // Owner master toggle fans out a per-stylist offer to every SE; the
  // per-stylist rows write individual offers; a stylist's own toggle is
  // their join (which needs a standing offer backend-side).
  if (patch.offerToSelfEmployed !== undefined) {
    teamForSharing().forEach((t) => {
      marketingBackend.loyaltyOffer(t.id, patch.offerToSelfEmployed === true)
        .catch((e) => console.error('loyalty offer failed:', e.message));
    });
  }
  if (patch.perStylist) {
    const before = prev.perStylist || {};
    Object.keys(patch.perStylist).forEach((id) => {
      const was = (before[id] || {}).offered === true;
      const is = (patch.perStylist[id] || {}).offered === true;
      if (was !== is) {
        marketingBackend.loyaltyOffer(id, is)
          .catch((e) => console.error('loyalty offer failed:', e.message));
      }
    });
  }
  if (patch.joinSalonScheme !== undefined) {
    marketingBackend.loyaltyJoin(patch.joinSalonScheme === true)
      .catch((e) => console.error('loyalty join failed:', e.message));
  }
  return next;
};

// Self-employed team members, for per-stylist consent. Sourced from the
// election hydrate (the backend SE roster — same one the marketing-settings
// page renders), cached under the sharing key. Falls back to the cached user
// object only if hydrate hasn't run yet this session.
export const teamForSharing = (ownerId) => {
  const cached = loadSharing(ownerId || currentOwnerId()).team;
  if (Array.isArray(cached)) return cached;
  try {
    const u = JSON.parse(localStorage.getItem('user')) || {};
    const team = (u.hairdresser && u.hairdresser.salon && (u.hairdresser.salon.employees || u.hairdresser.salon.hairdressers)) || u.employees || [];
    return team
      .filter((t) => t && t.employmentTypeId === 2)
      .map((t) => ({ id: String(t.id || t.userId || t.hairdresserId), name: t.name || `${t.firstName || ''} ${t.lastName || ''}`.trim() || 'Stylist' }));
  } catch (e) { return []; }
};

export const currentOwnerId = () => localStorage.getItem('userId');
